import React, { useEffect, useState } from "react";
import axios from "../../api/axiosInstance";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import GlobalLoader from "./GlobalLoader";
import FilterPillBar from "./FilterPillBar";
import { FaHeart, FaRegHeart, FaStar } from "react-icons/fa";
import { FiMoreVertical } from "react-icons/fi";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import EmptyStateCentered from "../static/EmptyStateCentered";
// import { getProfileSlug } from "..././api/slugify";
const NewMatch30Days = () => {
  const [matches, setMatches] = useState([]);
  const [favourites, setFavourites] = useState([]);
  const [interestsSent, setInterestsSent] = useState([]); // <-- Track interests
  const [loading, setLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState({});
  const [menuOpenId, setMenuOpenId] = useState(null);
  // const [isPaidMember, setIsPaidMember] = useState(true);

  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const calculateAge = (dob) => dayjs().diff(dayjs(dob), "year");

  const fetchMatches = async (filters = {}) => {
    try {
      setLoading(true);
      const res = await axios.get("api/matches/", { params: filters });
      // Filter profiles created in the last 30 days
      const recentMatches = res.data.filter(
        (match) => dayjs().diff(dayjs(match.created_at), "day") <= 30
      );

      setMatches(recentMatches);
      setLoading(false);
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error("Failed to fetch matches");
    }
    setLoading(false);
  };

  const fetchFavourites = async () => {
    try {
      const res = await axios.get("/api/favourites/", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setFavourites(res.data.map((fav) => fav.favourite_user));
    } catch (error) {
      console.error(error.response?.data || error.message);
    }
  };

  const fetchInterestsSent = async () => {
    try {
      const res = await axios.get("/api/interests/sent/", {
        // Adjust endpoint
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setInterestsSent(res.data.map((interest) => interest.receiver)); // receiver user ID
    } catch (error) {
      console.error(error.response?.data || error.message);
    }
  };

  useEffect(() => {
    fetchMatches();
    fetchFavourites();
    fetchInterestsSent();
  }, []);

  const sendInterest = async (receiverId) => {
    try {
      await axios.post(
        "/api/interests/",
        { receiver: receiverId },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      toast.success("Interest sent!");

      // Update has_sent_interest in UI
      setMatches((prev) =>
        prev.map((m) =>
          m.user.id === receiverId ? { ...m, has_sent_interest: true } : m
        )
      );
    } catch (error) {
      toast.error("Failed to send interest");
    }
  };

  const ignoreProfile = async (receiverId) => {
    try {
      await axios.post(
        "/api/interests/",
        { receiver: receiverId, status: "ignored" },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );
      toast.success("Profile ignored!");
      setMatches((prev) => prev.filter((m) => m.user.id !== receiverId));
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error("Failed to ignore profile");
    }
  };

  const toggleFavourite = async (receiverId) => {
    try {
      if (favourites.includes(receiverId)) {
        await axios.delete(`/api/favourites/${receiverId}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });
        setFavourites((prev) => prev.filter((id) => id !== receiverId));
        toast.success("Removed from favourites!");
      } else {
        await axios.post(
          "/api/favourites/",
          { favourite_user: receiverId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            },
          }
        );
        setFavourites((prev) => [...prev, receiverId]);
        toast.success("Added to favourites!");
      }
    } catch (error) {
      console.error(error.response?.data || error.message);
      toast.error("Failed to update favourite status");
    }
  };

  const handleBlockProfile = (receiverId) => {
    confirmAlert({
      title: "Confirm Block",
      message:
        "Are you sure you want to block this profile? You will not receive messages or see this profile again.",
      buttons: [
        {
          label: "Yes, Block",
          onClick: async () => {
            try {
              await axios.post(
                "/api/block-profile/",
                { blocked_id: receiverId },
                {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem(
                      "accessToken"
                    )}`,
                  },
                }
              );
              toast.success("Profile blocked successfully.");
              setMatches((prev) =>
                prev.filter((m) => m.user.id !== receiverId)
              );
            } catch (error) {
              console.error(error.response?.data || error.message);
              toast.error("Failed to block profile.");
            }
          },
        },
        {
          label: "Cancel",
          className: "bg-gray-200 text-gray-700",
        },
      ],
      closeOnEscape: true,
      closeOnClickOutside: true,
      overlayClassName: "bg-black bg-opacity-50",
    });
  };

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <div className="p-4 w-full mx-auto overflow-y-auto pt-16 sm:pt-16">
      <FilterPillBar
        activeFilters={activeFilters}
        setActiveFilters={setActiveFilters}
        onFilterChange={(filters) => fetchMatches(filters)}
      />

      {matches.length === 0 ? (
        <EmptyStateCentered message="No matches available at the moment." />
      ) : (
        <div className="flex flex-col gap-4">
          {matches.map((match) => {
            const hasSentInterest = match.has_sent_interest;

            return (
              <div
                key={match.id}
                className="bg-white w-full rounded shadow p-4 flex flex-col sm:flex-row items-center sm:items-start gap-4 relative"
              >
                <Link
                  to={`/profile/${match.user.id}`}
                  className="flex-shrink-0 relative"
                >
                  {dayjs().diff(dayjs(match.created_at), "day") <= 30 && (
                    <div className="absolute top-0 left-0 bg-pink-600 text-white text-xs font-semibold px-2 py-0.5 rounded shadow">
                      NEW
                    </div>
                  )}
                  <img
                    src={
                      match.photo || require("../../assets/default_image.webp")
                    }
                    alt={`${match.user.first_name}`}
                    className="w-32 h-32 object-cover rounded"
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavourite(match.user.id);
                    }}
                    className="absolute top-2 right-2 bg-white p-1 rounded-full shadow hover:bg-gray-100"
                  >
                    {favourites.includes(match.user.id) ? (
                      <FaHeart className="text-pink-600" />
                    ) : (
                      <FaRegHeart className="text-gray-500" />
                    )}
                  </button>
                </Link>
                <div className="absolute top-2 right-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(
                        menuOpenId === match.user.id ? null : match.user.id
                      );
                    }}
                    className="p-1 hover:bg-gray-100 rounded-full"
                  >
                    <FiMoreVertical size={18} />
                  </button>
                  {menuOpenId === match.user.id && (
                    <div className="absolute right-0 mt-2 w-40 bg-white border rounded shadow z-50">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBlockProfile(match.user.id);
                          setMenuOpenId(null);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      >
                        Block Profile
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <Link
                    to={`/profile/${match.user.id}`}
                    className="flex-shrink-0 text-decoration-none"
                  >
                    <p className="font-semibold text-lg">
                      {match.user.first_name} {match.user.last_name}
                      {match.profile_completed && (
                        <span
                          className="ml-3 text-yellow-500"
                          title="Profile 100% Completed"
                        >
                          <FaStar className="inline-block mb-1" />
                        </span>
                      )}
                      {match.is_paid_member && (
                        <span className="ml-2 px-2 py-1 text-xs bg-green-100 text-green-800 rounded">
                          Paid Member
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600 mb-2">
                      {calculateAge(match.dob)} yrs • {match.height_feet} ft '
                      {match.height_inches}" • {match.religion} •{" "}
                      {match.marital_status} • {match.highest_education} •{" "}
                      {match.employed_in} • {match.city}
                    </p>
                  </Link>
                  <div className="flex gap-2 mt-4">
                    {!match.has_sent_interest ? (
                      <>
                        <button
                          onClick={() => ignoreProfile(match.user.id)}
                          className="border border-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-100 transition"
                        >
                          Don't Show
                        </button>
                        <button
                          onClick={() => sendInterest(match.user.id)}
                          className="px-3 py-1 rounded bg-pink-600 text-white hover:bg-pink-700 transition"
                        >
                          Send Interest
                        </button>
                      </>
                    ) : (
                      <button
                        disabled
                        className="px-3 py-1 rounded bg-gray-300 text-gray-600 cursor-not-allowed"
                      >
                        Interest Sent
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NewMatch30Days;
